// §7 imagery frame — every real photo lands in here so the light grade
// (saturate .95 · contrast 1.03) and radius live in one place. If the
// image errors or is empty, we fall through to SceneFallback so the
// missing state looks intentional (§7.2).

import { useState } from "react";
import { SceneFallback, type SceneVariant } from "./SceneFallback";
import { sizedPhotoUrl, type ThumbSize } from "../lib/thumb";
import { logEvent } from "../lib/events";

export interface PhotoFrameProps {
  src?: string;
  alt?: string;
  /** Species scientific name — powers the SceneFallback silhouette. */
  hint?: string;
  /** Extra classes on the wrapper (size, aspect ratio). */
  className?: string;
  /** Tailwind radius class; defaults to rounded-thumb. */
  radiusClass?: string;
  /** Manual variant for the fallback. */
  variant?: SceneVariant;
  /** Object-fit override, "cover" by default. */
  objectFit?: "cover" | "contain";
  /**
   * Intrinsic pixel dimensions for the img tag. Setting these reserves
   * layout up-front (no CLS jitter when the photo late-decodes) without
   * affecting how the container sizes the image on screen. Defaults are
   * chosen for row/tile thumbs — override for heroes.
   */
  intrinsicWidth?: number;
  intrinsicHeight?: number;
  /**
   * Pass through to SceneFallback: use the cheap single-radial recipe
   * instead of the full god-rays + caustics stack. Turn on for row /
   * mini thumbs so a list of 14 photoless species doesn't burn the
   * compositor on scroll.
   */
  sceneSimple?: boolean;
  /**
   * iNaturalist size to request. `square` (~75 px) for row / mini thumbs,
   * `small` (~240 px) for tiles, `medium` (default) for the detail hero,
   * `large` for the full-viewer expansion. Applied only to iNat URLs;
   * other CDNs pass through untouched. Wire up per-primitive: SpeciesRow /
   * Mini pass "square", Tile passes "small", heroes leave it "medium".
   */
  size?: ThumbSize;
  /**
   * Browser fetch priority hint. Chromium + iOS 17+ Safari respect
   * fetchpriority on img tags. Set "high" on the first few
   * above-the-fold rows so those photos jump ahead of the default queue
   * without penalising the ones lower down.
   */
  fetchPriority?: "high" | "low" | "auto";
}

export function PhotoFrame({
  src,
  alt,
  hint,
  className = "",
  radiusClass = "rounded-thumb",
  variant,
  objectFit = "cover",
  intrinsicWidth = 156,
  intrinsicHeight = 156,
  sceneSimple = false,
  size = "medium",
  fetchPriority,
}: PhotoFrameProps) {
  const [errored, setErrored] = useState(false);
  const useScene = !src || errored;
  // Size the actual URL. Wikipedia / GBIF URLs come back as-is; iNat URLs
  // get their /medium.jpg suffix swapped to /square or /small.
  const sizedSrc = src ? sizedPhotoUrl(src, size) : undefined;

  return (
    <div
      className={`relative overflow-hidden ${radiusClass} ${className}`}
    >
      {useScene ? (
        <SceneFallback
          hint={hint}
          variant={variant}
          className="absolute inset-0"
          silhouetteClass="w-3/5 left-[20%] top-[18%]"
          vignette={false}
          simple={sceneSimple}
        />
      ) : (
        <img
          src={sizedSrc}
          alt={alt ?? ""}
          loading="lazy"
          decoding="async"
          // React 18 typings don't include the standard fetchpriority DOM
          // attribute yet; spread it through with a narrow cast so
          // Chromium + iOS 17+ Safari still pick it up. Silent no-op on
          // older browsers.
          {...(fetchPriority
            ? ({ fetchpriority: fetchPriority } as { fetchpriority: string })
            : {})}
          width={intrinsicWidth}
          height={intrinsicHeight}
          onError={() => {
            setErrored(true);
            logEvent("presentation.photo.fallback", {
              surface: "list",
              hint,
              photo_url: src,
              sized_url: sizedSrc,
              reason: "load-error",
            });
          }}
          className={`h-full w-full ${
            objectFit === "cover" ? "object-cover" : "object-contain"
          }`}
          // Light "house grade" — pulls mixed sources (iNat, Wikipedia,
          // GBIF media) toward the same tonal register. §7.1.
          style={{ filter: "saturate(.95) contrast(1.03)" }}
        />
      )}
    </div>
  );
}
